import React, { useCallback, useEffect, useRef, useState } from 'react'
import ContentEditable from 'react-contenteditable'
import { Button, Col, Container, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import styles from 'components/EditableField/editable-field.module.scss'
import clsx from 'clsx'
import OutsideClickHandler from 'react-outside-click-handler'
import i18n from 'i18next'

function setEndOfContenteditable(contentEditableElement) {
  let range, selection

  if (document.createRange) {
    //Firefox, Chrome, Opera, Safari, IE 9+
    range = document.createRange() //Create a range (a range is a like the selection but invisible)
    range.selectNodeContents(contentEditableElement) //Select the entire contents of the element with the range
    range.collapse(false) //collapse the range to the end point. false means collapse to end rather than the start
    selection = window.getSelection() //get the selection object (allows you to change selection)
    selection.removeAllRanges() //remove any selections already made
    selection.addRange(range) //make the range you have just created the visible selection

    // Check if there are selection ranges
    if (!selection.rangeCount) {
      return
    }

    // Get the first selection range. There's almost never can be more (instead of firefox)
    const firstRange = selection.getRangeAt(0)

    // Sometimes if the editable element is getting removed from the dom you may get a HierarchyRequest error in safari
    if (firstRange.commonAncestorContainer === document) {
      return
    }

    // Create an empty br that will be used as an anchor for scroll, because it's imposible to do it with just text nodes
    const tempAnchorEl = document.createElement('br')

    // Put the br right after the caret position
    firstRange.insertNode(tempAnchorEl)

    // Remove the anchor because it's not needed anymore
    tempAnchorEl.remove()
  } else if (document.selection) {
    //IE 8 and lower
    range = document.body.createTextRange() //Create a range (a range is a like the selection but invisible)
    range.moveToElementText(contentEditableElement) //Select the entire contents of the element with the range
    range.collapse(false) //collapse the range to the end point. false means collapse to end rather than the start
    range.select() //Select the range (make it the visible selection
  }
}

const EditableField = ({
  disabled = false,
  color = 'inherit',
  editModeColor = 'inherit',
  id,
  name,
  login,
  apply,
  className,
  editIconClassName,
  children,
  placeholder = i18n.t('services::account_list.edit_account_name.placeholder'),
  DisabledButtonComponent = null,
}) => {
  const EditableRef = useRef()
  const [val, setVal] = useState(name ?? login)
  const [submitting, setSubmitting] = useState(false)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!editing) return

    setEndOfContenteditable(EditableRef.current?.el.current)

    return () => {
      EditableRef.current = false
    }
  }, [editing])

  useEffect(() => {
    if (editing) return
    setVal(name ?? login)
  }, [name, login, editing])

  const applyValue = useCallback(async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)

    try {
      await apply(id, EditableRef.current?.el.current.innerText.trim() || login)

      setSubmitting(false)
      setEditing(false)
    } catch (error) {
      setSubmitting(false)
    }
  }, [val, submitting, setSubmitting, EditableRef.current])

  return (
    <Grid
      gap={8}
      wrap={'nowrap'}
      justifyContent="start"
      alignItems="center"
      className={clsx('edit-field', styles['edit-field'], className)}
    >
      {!editing ? (
        <>
          <Typography
            variant={'none'}
            component={
              <Col
                flexGrow={0}
                className={styles['overflow-container']}
                title={val}
              >
                {children}
              </Col>
            }
            color={color}
          />
          {!disabled ? (
            <Col flexGrow={1}>
              <Container
                display={'flex'}
                flexAlign={'center'}
              >
                <Button
                  noLabel
                  variant={'ghost'}
                  grey
                  link
                  size={'s'}
                  startIcon={<Icon name={'edit'} />}
                  noHover
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditing(true)
                  }}
                  className={clsx(styles['edit-icon'], editIconClassName)}
                />
              </Container>
            </Col>
          ) : (
            DisabledButtonComponent && (
              <DisabledButtonComponent className={clsx(styles['edit-icon'], editIconClassName)} />
            )
          )}
        </>
      ) : (
        <>
          <Col
            flexGrow={0}
            className={clsx(styles['input-container'], 'input-container')}
          >
            <OutsideClickHandler
              onOutsideClick={(e) => {
                if (e.target.closest('.edit-field')) {
                  return false
                }
                editing && setEditing(false)
              }}
            >
              {React.cloneElement(
                children,
                {},
                <ContentEditable
                  color={editModeColor}
                  className={clsx('input', styles['content-editable'])}
                  disabled={submitting}
                  onKeyDown={(e) => (e.keyCode === 13 || e.keyCode === 9) && applyValue()}
                  placeholder={placeholder}
                  ref={EditableRef}
                  tagName={'div'}
                  html={val}
                  onChange={(e) => setVal(e.target.value.trim().replace(/<[^>]*>/g, ''))}
                  onPaste={(e) => {
                    e.preventDefault()
                    let contentOnBlur = (e.originalEvent || e).clipboardData
                      .getData('Text')
                      .replace(/(<([^>]+)>)/gi, '')
                      .replace(/[\n]/g, ' ')
                      .trim()

                    document.execCommand('insertText', false, contentOnBlur)
                  }}
                />,
              )}
            </OutsideClickHandler>
          </Col>
          <Col flexGrow={1}>
            <Grid
              gap={4}
              alignItems={'center'}
              className={className}
              wrap={'nowrap'}
            >
              {submitting ? (
                <Spinner
                  width={28}
                  className={clsx(styles['edit-icon'], styles['edit-icon_spinner'])}
                  color={'inherit'}
                />
              ) : (
                <>
                  <Button
                    noLabel
                    variant={'ghost'}
                    grey
                    link
                    size={'s'}
                    startIcon={<Icon name={'close'} />}
                    noHover
                    onClick={() => {
                      setVal(name ?? login)
                      setEditing(false)
                    }}
                    className={styles['edit-icon']}
                  />
                  <Button
                    noLabel
                    variant={'ghost'}
                    grey
                    link
                    size={'s'}
                    startIcon={<Icon name={'check'} />}
                    noHover
                    className={styles['edit-icon']}
                    onClick={applyValue}
                  />
                </>
              )}
            </Grid>
          </Col>
        </>
      )}
    </Grid>
  )
}

EditableField.propTypes = {
  disabled: PropTypes.bool,
  color: PropTypes.string,
  editModeColor: PropTypes.string,
  children: PropTypes.node.isRequired,
  editing: PropTypes.bool,
  className: PropTypes.string,
  editIconClassName: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string,
  login: PropTypes.string,
  apply: PropTypes.func,
  close: PropTypes.func,
  placeholder: PropTypes.string,
  DisabledButtonComponent: PropTypes.element,
}

export default EditableField
